import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSmile, faSadTear, faCloudRain, faCalendarAlt, faClock, faUser, faExclamationCircle
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import api from "../../../api/api"; 

const CancelWalk = ({ walk, isOpen, onCancel, userUpdateContext }) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scheduledReasons = [
    { label: "Change in plans", icon: faCalendarAlt },
    { label: "No longer need the service", icon: faSmile },
    { label: "Found alternative walker", icon: faUser },
    { label: "Schedule conflict", icon: faClock },
    { label: "Other", icon: faExclamationCircle },
  ];

  const instantReasons = [
    { label: "Walker unavailable", icon: faUser },
    { label: "Bad weather conditions", icon: faCloudRain },
    { label: "Pet not feeling well", icon: faSadTear },
    { label: "Unexpected commitment", icon: faCalendarAlt },
    { label: "Schedule conflict", icon: faClock },
    { label: "Other", icon: faExclamationCircle },
  ];

  const reasons =
    walk && walk.scheduled_at
      ? scheduledReasons
      : instantReasons;

  const handleCancel = async () => {
    setIsSubmitting(true);
    const cancelReason = selectedReason === "Other" ? customReason : selectedReason;
    try {
      await api.post(`/walks/${walk.id}`, {
        _method: 'PUT',
        walkCancelUser: true,
        cancel_reason: cancelReason
      });
      toast.success("Walk canceled successfully.");
      userUpdateContext();
      closeModal();

    } catch (error) {
      toast.error("Failed to cancel walk.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setSelectedReason("");
    setCustomReason("");
    onCancel();
  };

  return (
    <>
      <Transition appear show={isOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          {/* Background overlay */}
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
          </Transition.Child>

          {/* Modal panel */}
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-2xl font-bold text-purple-700 mb-4">
                    Are you sure you want to cancel? 🐾
                  </Dialog.Title>

                  {/* Encouraging message */}
                  <p className="text-gray-600 mb-6">
                    Your furry friend is excited for their walk! 🌞 Walking is great for their mood and health. Why not keep the booking and brighten their day? 🐕
                  </p>

                  {/* Reconsideration message */}
                  <div className="bg-purple-50 p-4 rounded-md mb-6 flex items-center space-x-4">
                    <FontAwesomeIcon icon={faSmile} className="text-purple-500 text-3xl" />
                    <p className="text-purple-700">
                      Remember: Regular walks help reduce stress and boost happiness for your pet. They're counting on you! 🥰
                    </p>
                  </div>

                  {/* Reason selection */}
                  <h4 className="text-lg font-semibold text-gray-700 mb-3">Why do you want to cancel?</h4>
                  <div className="space-y-2">
                    {reasons.map((reason, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedReason(reason.label)}
                        className={`flex items-center w-full p-3 rounded-md border ${selectedReason === reason.label ? 'bg-purple-100 border-purple-500' : 'border-gray-300'} hover:bg-purple-50`}
                      >
                        <FontAwesomeIcon icon={reason.icon} className="text-purple-500 mr-3" />
                        <span className="text-gray-700">{reason.label}</span>
                      </button>
                    ))}
                    {selectedReason === "Other" && (
                      <textarea
                        placeholder="Enter custom reason"
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                        className="w-full mt-2 px-3 py-2 border rounded-md focus:outline-none focus:border-purple-500"
                      />
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={closeModal}
                      className="bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 text-gray-700"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={!selectedReason || (selectedReason === "Other" && !customReason)}
                      className={`px-4 py-2 rounded-lg text-white ${isSubmitting ? "bg-red-400" : "bg-red-500 hover:bg-red-600"}`}
                    >
                      {isSubmitting ? "Cancelling..." : "Confirm Cancellation"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default CancelWalk;
